import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RiBuildingLine,
  RiTeamLine,
  RiPercentLine,
  RiCalendarCheckLine,
  RiHistoryLine,
  RiMapPin2Line,
  RiFileList3Line,
  RiFileTextLine,
  RiDeleteBinLine,
  RiMailLine,
} from 'react-icons/ri';
import companyService from '../../services/companyService';
import siteService, { Site } from '../../services/siteService';
import rentalService, { Rental } from '../../services/rentalService';
import quotationService from '../../services/quotationService';
import type { Quotation } from '../../types/quotation';
import { useDeleteCompany } from '../../hooks/mutations/useCompanyMutations';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { COMPANIES_PATH, companyEditUrl } from '../../utils/companyLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert, showConfirm } from '../../utils/alert';
import { formatDateTimeFull } from '../../utils/dateUtils';
import { getRentalStatusDisplay } from '../../utils/rentalStatusLabel';
import { quotationStatusLabel } from '../../utils/quotationActionOutcome';
import { siteDetailUrl, siteListByCompanyUrl } from '../../utils/siteLinks';
import { companyToForm, WHT_RATE_LABELS, COMPANY_FIELD_LABELS } from '../../components/company/companyForm';
import {
  DetailPageLayout,
  DetailSection,
  InfoField,
  InfoGrid,
  RecordMetaCard,
  EntityActivityTimeline,
  RelatedRecordsCard,
  useDetailNavigation,
  detailNavState,
  headerButtonClass,
  headerButtonVariants,
} from '../../components/detail';
import { cn } from '../../utils/cn';

const LIST_PATH = COMPANIES_PATH;
const RELATED_LIMIT = 10;

const money = (n: unknown) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
};

/**
 * หน้ารายละเอียดบริษัท — `/master-data/companies/:id` (แทนโมดัล "รายละเอียดบริษัท" · มติ PO 28 ต.ค. 2026)
 * ข้อมูลครบทุกช่องของฟอร์มเดิม (อ่านอย่างเดียว) + การ์ดข้อมูลที่เกี่ยวข้อง (โครงการ / สัญญาเช่า / ใบเสนอราคา
 * แต่ละใบ gate ด้วยสิทธิ์ดูของปลายทางเอง) + ข้อมูลการสร้าง/แก้ไข + ประวัติกิจกรรม
 */
const CompanyViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const companyId = parseRouteId(id);
  const location = useLocation();
  const { hasPermission } = usePermission();
  const { backTo, goTo, navState } = useDetailNavigation(LIST_PATH);
  const deleteCompanyMutation = useDeleteCompany();
  const [activityRefresh, setActivityRefresh] = useState(0);

  const { data: company, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.companies.detail(companyId ?? 0),
    queryFn: () => companyService.getCompany(companyId!),
    enabled: companyId !== null,
  });

  // ลิงก์จากการ์ดที่เกี่ยวข้องไปหน้ารายละเอียดของมัน แล้วกด "กลับ" ต้องกลับมาหน้าบริษัทนี้
  const hereState = detailNavState(location);

  const handleRefresh = () => {
    refetch();
    setActivityRefresh((n) => n + 1);
  };

  const handleDelete = () => {
    if (!company) return;
    showConfirm(
      'ยืนยันการลบบริษัท',
      `คุณต้องการลบบริษัท ${company.name} ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้ และอาจส่งผลกระทบต่อข้อมูลที่เกี่ยวข้อง`,
      async () => {
        try {
          const result = await deleteCompanyMutation.mutateAsync(company.id);
          if (result && result.success === false) {
            showAlert('ไม่สามารถลบบริษัท', result.message || 'ไม่สามารถลบบริษัทได้', 'warning');
            return;
          }
          goTo(backTo, { replace: true });
        } catch (e) {
          // useMutationWithRefresh แสดงข้อความจาก server แล้ว (เช่น มีโครงการ/การเช่าผูกอยู่)
          console.error('Error deleting company:', e);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  const notFound = companyId === null ? { response: { status: 404 } } : null;
  const form = company ? companyToForm(company) : null;
  const isActive = form?.is_active ?? true;

  return (
    <DetailPageLayout
      breadcrumbs={[{ label: 'ข้อมูลบริษัท', path: backTo }, { label: company?.name || 'รายละเอียด' }]}
      icon={<RiBuildingLine />}
      title={company?.name || 'รายละเอียดบริษัท'}
      recordId={company?.code}
      subtitle="รายละเอียดบริษัทลูกค้า"
      backTo={backTo}
      onRefresh={handleRefresh}
      editTo={company ? companyEditUrl(company.id) : undefined}
      editPermission="companies.edit"
      editState={navState}
      actions={
        <>
          {/* ลิงก์ข้ามโมดูล (G-12: gate quotations.view) — หน้ารายการใบเสนอราคากรองตามบริษัทจาก ?company= */}
          {company && hasPermission('quotations.view') && (
            <Link
              to={`/billing/quotations?company=${company.id}`}
              className={cn(headerButtonClass, headerButtonVariants.neutral)}
            >
              <RiFileTextLine className="h-4 w-4 mr-1.5" />
              ใบเสนอราคาของบริษัทนี้
            </Link>
          )}
          {company && hasPermission('companies.delete') && (
            <button type="button" onClick={handleDelete} className={cn(headerButtonClass, headerButtonVariants.danger)}>
              <RiDeleteBinLine className="h-4 w-4 mr-1.5" />
              ลบ
            </button>
          )}
        </>
      }
      loading={companyId !== null && isLoading}
      error={notFound || error}
      notFoundMessage="ไม่พบบริษัทที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => refetch()}
      sidebar={
        company && (
          <>
            <RecordMetaCard
              createdAt={company.createdAt}
              createdBy={company.created_by}
              updatedAt={company.updatedAt}
              updatedBy={company.updated_by}
              extra={company.syncedFromFlow ? [{ label: 'ซิงค์จาก Flow Account', value: formatDateTimeFull(company.flowaccountSyncDate) }] : []}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="company"
                entityId={company.id}
                refreshTrigger={activityRefresh}
                fieldLabels={COMPANY_FIELD_LABELS}
                valueFormatters={{
                  is_active: (v) => (v ? 'ใช้งาน' : 'ไม่ใช้งาน'),
                  default_wht_rate: (v) => WHT_RATE_LABELS[Number(v)] ?? `${v}%`,
                  deposit_amount: (v) => `${money(v)} บาท`,
                }}
              />
            </DetailSection>
          </>
        )
      }
    >
      {company && form && (
        <>
          <DetailSection
            title="ข้อมูลพื้นฐานบริษัท"
            icon={<RiBuildingLine />}
            actions={
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
                  isActive ? 'bg-success-50 text-success-700 border-success-200' : 'bg-secondary-50 text-secondary-600 border-secondary-200'
                )}
                data-testid="company-status-badge"
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', isActive ? 'bg-success-500' : 'bg-secondary-400')} />
                {isActive ? 'ใช้งาน' : 'ไม่ใช้งาน'}
              </span>
            }
          >
            <InfoGrid>
              <InfoField label="รหัสบริษัท" value={form.code} mono />
              <InfoField label="ชื่อบริษัท" value={form.name} />
              <InfoField label="เลขประจำตัวผู้เสียภาษี" value={form.taxId} mono />
              <InfoField label="ประเภทลูกค้า" value={form.customer_type} />
              <InfoField label="ที่อยู่" value={form.address} multiline fullWidth />
            </InfoGrid>
          </DetailSection>

          <DetailSection title="ข้อมูลติดต่อ" icon={<RiTeamLine />}>
            <InfoGrid>
              <InfoField label="ผู้ติดต่อ" value={form.contactPerson} />
              <InfoField label="เบอร์โทรศัพท์" value={form.phone} mono />
              <InfoField label="อีเมล" value={form.email}>
                {form.email ? (
                  <a href={`mailto:${form.email}`} className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 [overflow-wrap:anywhere]">
                    <RiMailLine className="h-4 w-4 flex-shrink-0" />
                    {form.email}
                  </a>
                ) : undefined}
              </InfoField>
              <InfoField
                label="พนักงานขายที่ดูแล"
                value={company.sales_person?.full_name || company.sales_person?.username || (form.sales_person_id ? `#${form.sales_person_id}` : null)}
              />
            </InfoGrid>
          </DetailSection>

          <DetailSection title="การตั้งค่าภาษีและวางบิล" icon={<RiCalendarCheckLine />}>
            <InfoGrid>
              <InfoField
                label="อัตราหัก ณ ที่จ่าย Default"
                value={WHT_RATE_LABELS[Number(form.default_wht_rate ?? 0)] ?? `${form.default_wht_rate}%`}
                hint="ใช้เป็นค่า default เมื่อสร้างใบแจ้งหนี้ใหม่สำหรับบริษัทนี้"
              />
              <InfoField label="วันออกบิล" value={form.billing_day ? `วันที่ ${form.billing_day}` : null} />
              <InfoField label="ระยะเวลาชำระเงิน" value={form.payment_terms ? `${form.payment_terms} วัน` : null} />
              <InfoField
                label="วันครบกำหนดชำระ (ของเดือนถัดไป)"
                value={form.due_day_of_next_month ? `วันที่ ${form.due_day_of_next_month}` : 'ไม่กำหนด (ใช้ระยะเวลาชำระเงิน)'}
              />
            </InfoGrid>
          </DetailSection>

          <DetailSection title="การตั้งค่ามัดจำ" icon={<RiPercentLine />}>
            <InfoGrid>
              <InfoField
                label="ยอดค่ามัดจำเก็บสูงสุด"
                value={Number(form.depositSettings?.deposit_amount) > 0 ? `${money(form.depositSettings?.deposit_amount)} บาท` : 'ไม่จำกัด'}
                hint="เพดานวงเงินมัดจำสะสมของบริษัท (0/ว่าง = ไม่จำกัด)"
              />
            </InfoGrid>
          </DetailSection>

          <RelatedRecordsCard<Site>
            title="โครงการของบริษัทนี้"
            icon={<RiMapPin2Line />}
            permission="sites.view"
            queryKey={['company-related', company.id, 'sites']}
            fetcher={() => siteService.getSites({ company_id: company.id, limit: RELATED_LIMIT })}
            rowKey={(s) => s.id}
            rowTo={(s) => siteDetailUrl(s.id, company.id)}
            rowState={hereState}
            viewAllTo={siteListByCompanyUrl(company.id)}
            emptyText="บริษัทนี้ยังไม่มีโครงการ"
            testId="company-related-sites"
            renderRow={(s) => (
              <>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-secondary-900 [overflow-wrap:anywhere]">{s.display_name || s.name}</p>
                  <p className="text-xs text-secondary-500 font-mono">{s.code}</p>
                </div>
                <span className={cn('text-xs flex-shrink-0', s.status === 'inactive' ? 'text-secondary-500' : 'text-success-700')}>
                  {s.status === 'inactive' ? 'ไม่ใช้งาน' : 'ใช้งาน'}
                </span>
              </>
            )}
          />

          <RelatedRecordsCard<Rental>
            title="สัญญาเช่าของบริษัทนี้"
            icon={<RiFileList3Line />}
            permission="rentals.view"
            queryKey={['company-related', company.id, 'rentals']}
            fetcher={() => rentalService.getRentals({ company_id: company.id, limit: RELATED_LIMIT })}
            rowKey={(r) => r.id}
            rowTo={(r) => `/rentals/view/${r.id}`}
            rowState={hereState}
            emptyText="บริษัทนี้ยังไม่มีรายการเช่า"
            testId="company-related-rentals"
            renderRow={(r) => {
              const st = getRentalStatusDisplay(r.status);
              return (
                <>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary-900 font-mono">{r.rental_number}</p>
                    <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">{r.site_name || r.site?.name || '-'}</p>
                  </div>
                  <span className={cn('px-2 py-0.5 rounded-full text-xs flex-shrink-0', st.badgeColor)}>{st.label}</span>
                </>
              );
            }}
          />

          <RelatedRecordsCard<Quotation>
            title="ใบเสนอราคาของบริษัทนี้"
            icon={<RiFileTextLine />}
            permission="quotations.view"
            queryKey={['company-related', company.id, 'quotations']}
            fetcher={() => quotationService.getQuotations({ company_id: company.id, limit: RELATED_LIMIT })}
            rowKey={(q) => q.id}
            rowTo={(q) => `/billing/quotations/${q.id}`}
            rowState={hereState}
            viewAllTo={`/billing/quotations?company=${company.id}`}
            emptyText="บริษัทนี้ยังไม่มีใบเสนอราคา"
            testId="company-related-quotations"
            renderRow={(q) => (
              <>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-secondary-900 font-mono">{q.quotation_number}</p>
                  <p className="text-xs text-secondary-500">{quotationStatusLabel(q.status)}</p>
                </div>
                <span className="text-sm text-secondary-900 tabular-nums flex-shrink-0">{money(q.total_amount)}</span>
              </>
            )}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export default CompanyViewPage;
